import { countdownLabel, diffDays, formatDate, sessionPhase, type Category, type PresenterRef, type Session } from "@love-cpmd/core";
import { Avatar, Badge, cn, type BadgeTone } from "@love-cpmd/ui";
import {
  ArrowLeftRight,
  BookOpen,
  Briefcase,
  CalendarClock,
  CircleCheck,
  CircleX,
  Cpu,
  Factory,
  HardHat,
  Hourglass,
  Lightbulb,
  RotateCw,
  Shapes,
  Sparkles,
  Sprout,
  Tag,
  Timer,
  UserRoundCog,
  type LucideIcon,
} from "lucide-react";
import { useId } from "react";
import type { World } from "../../lib/queries";

/* ───────── Presenter ───────── */

export function PresenterAvatar({
  presenter,
  world,
  size = "md",
  className,
  ring,
}: {
  presenter: PresenterRef | undefined;
  world: World;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
  ring?: boolean;
}) {
  const member = presenter?.kind === "member" ? world.membersById.get(presenter.id) : undefined;
  const name = world.name(presenter);
  return <Avatar name={name} seed={member?.memberId ?? name} src={member?.avatarUrl} size={size} className={className} ring={ring} />;
}

/** "CPMD003 · Member", or "Guest presenter". */
export function presenterMeta(presenter: PresenterRef | undefined, world: World): string {
  if (!presenter) return "";
  if (presenter.kind === "member") {
    const m = world.membersById.get(presenter.id);
    if (!m) return "Former member";
    return m.position ? `${m.memberId} · ${m.position}` : m.memberId;
  }
  if (presenter.kind === "guest") return world.guestsById.get(presenter.id)?.note || "Guest presenter";
  return "Guest presenter";
}

/* ───────── Categories ───────── */

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  "cat-industry": Factory,
  "cat-tech": Cpu,
  "cat-selfdev": Sprout,
  "cat-management": Briefcase,
  "cat-productivity": Timer,
  "cat-workexp": HardHat,
  "cat-knowledge": BookOpen,
  "cat-interesting": Lightbulb,
  "cat-others": Shapes,
};

export function categoryIcon(categoryId: string | null | undefined): LucideIcon {
  return (categoryId && CATEGORY_ICONS[categoryId]) || Tag;
}

export function CategoryChip({
  category,
  size = "sm",
  className,
  short,
}: {
  category: Category | undefined;
  size?: "xs" | "sm" | "md";
  className?: string;
  short?: boolean;
}) {
  if (!category) {
    return (
      <Badge tone="outline" size={size} shape="tag" className={className}>
        Category not set
      </Badge>
    );
  }
  const Icon = categoryIcon(category.id);
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-md font-semibold whitespace-nowrap text-on-surface",
        size === "xs" ? "h-5 px-1.5 text-[10px]" : size === "sm" ? "h-6 px-2 text-label-sm" : "h-7 px-2.5 text-label-md",
        className,
      )}
      style={{ backgroundColor: `${category.color}1f` }}
    >
      <Icon className={cn("shrink-0", size === "md" ? "size-4" : "size-3.5")} style={{ color: category.color }} aria-hidden />
      {short ? category.shortName : category.name}
    </span>
  );
}

export function CategoryDot({ color, className }: { color: string; className?: string }) {
  return <span aria-hidden className={cn("inline-block size-2 shrink-0 rounded-full", className)} style={{ backgroundColor: color }} />;
}

/** Decorative tile used where the mockups had stock photos: category color + icon. */
export function SessionArt({ category, className, label }: { category?: Category; className?: string; label?: string }) {
  const Icon = categoryIcon(category?.id);
  const color = category?.color ?? "#7a1f3d";
  const patternId = `dots-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <div
      className={cn("relative overflow-hidden rounded-xl", className)}
      style={{
        background: `radial-gradient(120% 90% at 100% 0%, ${color}33 0%, transparent 55%), linear-gradient(145deg, ${color} 0%, #3f0018 100%)`,
      }}
      aria-hidden={!label}
    >
      <svg className="absolute inset-0 h-full w-full opacity-[0.12]" aria-hidden>
        <defs>
          <pattern id={patternId} width="14" height="14" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.2" fill="#fff" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${patternId})`} />
      </svg>
      <Icon className="absolute -right-3 -bottom-4 size-28 text-white/15" strokeWidth={1.25} aria-hidden />
      <div className="absolute top-3 left-3 flex size-10 items-center justify-center rounded-xl bg-white/15 text-white backdrop-blur-sm">
        <Icon className="size-5" aria-hidden />
      </div>
      {label && (
        <span className="absolute bottom-3 left-3 rounded-md bg-white/90 px-2 py-0.5 text-label-sm font-semibold text-on-surface">{label}</span>
      )}
    </div>
  );
}

/* ───────── Status ───────── */

const STATUS_META = {
  scheduled: { label: "Scheduled", tone: "scheduled" as BadgeTone, Icon: CalendarClock },
  completed: { label: "Completed", tone: "completed" as BadgeTone, Icon: CircleCheck },
  cancelled: { label: "Cancelled", tone: "cancelled" as BadgeTone, Icon: CircleX },
};

export function StatusBadge({ session, size = "sm" }: { session: Session; size?: "xs" | "sm" | "md" }) {
  const meta = STATUS_META[session.status];
  return (
    <Badge tone={meta.tone} size={size} icon={<meta.Icon aria-hidden />}>
      {meta.label}
    </Badge>
  );
}

export function TypeBadge({ session, size = "sm" }: { session: Session; size?: "xs" | "sm" | "md" }) {
  if (session.type === "replacement")
    return (
      <Badge tone="replacement" size={size} icon={<UserRoundCog aria-hidden />}>
        Replacement
      </Badge>
    );
  if (session.type === "spontaneous")
    return (
      <Badge tone="spontaneous" size={size} icon={<Sparkles aria-hidden />}>
        Spontaneous
      </Badge>
    );
  return null;
}

/** History labels (decision #7): shown beside the status, not as a status. */
export function HistoryBadges({ session, size = "sm" }: { session: Session; size?: "xs" | "sm" | "md" }) {
  const last = session.labels.at(-1);
  if (!last) return null;
  return last.kind === "swapped" ? (
    <Badge tone="swap" size={size} icon={<ArrowLeftRight aria-hidden />}>
      Swapped
    </Badge>
  ) : (
    <Badge tone="rescheduled" size={size} icon={<RotateCw aria-hidden />}>
      Rescheduled
    </Badge>
  );
}

export function SessionBadges({ session, size = "sm", hideStatus }: { session: Session; size?: "xs" | "sm" | "md"; hideStatus?: boolean }) {
  return (
    <>
      {!hideStatus && <StatusBadge session={session} size={size} />}
      <TypeBadge session={session} size={size} />
      {session.status === "scheduled" && <HistoryBadges session={session} size={size} />}
    </>
  );
}

export function CountdownBadge({ date, today, size = "sm" }: { date: string; today: string; size?: "xs" | "sm" | "md" }) {
  const diff = diffDays(date, today);
  const label = countdownLabel(date, today);
  return (
    <Badge tone={diff === 0 ? "brand" : diff > 0 && diff <= 3 ? "primary" : "secondary"} size={size} icon={<Hourglass aria-hidden />}>
      {label}
    </Badge>
  );
}

/** One-line description of the session phase, used under dates. */
export function phaseHint(session: Session, today: string): string {
  switch (sessionPhase(session, today)) {
    case "upcoming":
      return countdownLabel(session.date, today);
    case "today":
      return "Today";
    case "overdue":
      return "Waiting to be marked done";
    case "completed":
      return `Completed ${formatDate(session.date, "dayMonth")}`;
    case "cancelled":
      return session.cancelReason ? `Cancelled — ${session.cancelReason}` : "Cancelled";
  }
}
